// Archive de Grammar Bank: listado con filtros nivel + categoría + búsqueda libre.
// Admin: botón "Nuevo" abre el drawer editor.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/atoms/Badge';
import { Button } from '@/components/atoms/Button';
import { Icon } from '@/components/atoms/Icon';
import { Spinner } from '@/components/atoms/Spinner';
import { Header } from '@/components/organisms/Header';
import { GrammarBankEditorDrawer } from '@/components/organisms/GrammarBankEditorDrawer';
import { useAuth } from '@/context/AuthContext';
import { ApiError } from '@/services/api';
import { listLevels, type CatalogLevel } from '@/services/catalogs';
import {
  getCatalogs,
  listGrammarBanks,
  type GrammarBankListItem,
  type GrammarCategory,
} from '@/services/grammar-banks';
import styles from './GrammarBank.module.scss';

function stripHtml(html: string | null, max = 200): string {
  if (!html) return '';
  const stripped = html.replace(/<!--[\s\S]*?-->/g, '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  return stripped.length > max ? stripped.slice(0, max).trimEnd() + '…' : stripped;
}

export function GrammarBankArchive() {
  const { state } = useAuth();
  const isAdmin = state.status === 'authenticated' && state.user.roles.includes('admin');

  const [items, setItems] = useState<GrammarBankListItem[]>([]);
  const [levels, setLevels] = useState<CatalogLevel[]>([]);
  const [categories, setCategories] = useState<GrammarCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [levelFilter, setLevelFilter] = useState<number | undefined>(undefined);
  const [categoryFilter, setCategoryFilter] = useState<number | undefined>(undefined);
  const [search, setSearch] = useState('');

  const [editorOpen, setEditorOpen] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await listGrammarBanks({
        level_id: levelFilter,
        category_id: categoryFilter,
        search: search.trim() || undefined,
      });
      setItems(rows);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Error');
    } finally {
      setLoading(false);
    }
  }, [levelFilter, categoryFilter, search]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([listLevels(), getCatalogs()])
      .then(([lvls, cat]) => {
        if (cancelled) return;
        setLevels(lvls);
        setCategories(cat.categories);
      })
      .catch(() => {
        /* ignore */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const hasFilters = useMemo(
    () => Boolean(levelFilter || categoryFilter || search.trim()),
    [levelFilter, categoryFilter, search]
  );

  function clearFilters() {
    setLevelFilter(undefined);
    setCategoryFilter(undefined);
    setSearch('');
  }

  return (
    <>
      <Header
        title="Grammar Bank"
        actions={
          isAdmin ? (
            <Button variant="primary" onClick={() => setEditorOpen(true)}>
              <Icon name="plus" size={14} /> Nuevo
            </Button>
          ) : null
        }
      />
      <main className={styles.main}>
        <p className={styles.lead}>
          Recursos de gramática organizados por nivel y categoría. Cada ficha tiene una sección de teoría y ejercicios.
        </p>

        <div className={styles.filters}>
          <label className={styles.filterField}>
            <span className={styles.filterLabel}>Nivel</span>
            <select
              value={levelFilter ?? ''}
              onChange={(e) => setLevelFilter(e.target.value ? Number(e.target.value) : undefined)}
            >
              <option value="">Todos</option>
              {levels.map((l) => (
                <option key={l.id} value={l.id}>{l.label}</option>
              ))}
            </select>
          </label>
          <label className={styles.filterField}>
            <span className={styles.filterLabel}>Categoría</span>
            <select
              value={categoryFilter ?? ''}
              onChange={(e) => setCategoryFilter(e.target.value ? Number(e.target.value) : undefined)}
            >
              <option value="">Todas</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
          </label>
          <label className={styles.filterField}>
            <span className={styles.filterLabel}>Buscar</span>
            <input
              type="search"
              placeholder="Buscar por título…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          {hasFilters && (
            <Button variant="secondary" size="sm" onClick={clearFilters}>
              Limpiar
            </Button>
          )}
        </div>

        {loading ? (
          <div className={styles.center}>
            <Spinner size="lg" />
          </div>
        ) : error ? (
          <p className={styles.error}>{error}</p>
        ) : items.length === 0 ? (
          <div className={styles.empty}>
            <Icon name="book" size={32} />
            <h3>{hasFilters ? 'Sin resultados' : 'Aún no hay fichas de gramática'}</h3>
            <p>
              {hasFilters
                ? 'Prueba a quitar algún filtro para ver más resultados.'
                : isAdmin
                  ? 'Crea la primera ficha con el botón "Nuevo".'
                  : 'En cuanto el admin añada fichas, aparecerán aquí.'}
            </p>
          </div>
        ) : (
          <ul className={styles.cardGrid}>
            {items.map((it) => (
              <li key={it.id} className={styles.card}>
                <Link to={`/grammar-bank/${it.slug}`} className={styles.cardLink}>
                  <div className={styles.cardHeader}>
                    {it.level_label && <Badge tone="accent">{it.level_label}</Badge>}
                    {it.category_label && <Badge tone="neutral">{it.category_label}</Badge>}
                    {it.is_published === 0 && <Badge tone="warning">Borrador</Badge>}
                  </div>
                  <h3 className={styles.cardTitle}>{it.title}</h3>
                  {it.content && <p className={styles.cardExcerpt}>{stripHtml(it.content, 180)}</p>}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </main>

      {isAdmin && (
        <GrammarBankEditorDrawer
          open={editorOpen}
          onOpenChange={setEditorOpen}
          mode="create"
          onSaved={() => {
            setEditorOpen(false);
            void reload();
          }}
        />
      )}
    </>
  );
}
